import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, Loader2, RotateCcw } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../components/Reveal";
import { SITE } from "../lib/site";
import { api } from "../lib/api";

const INITIAL = {
  first_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const inputCls = (invalid) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-brand-navy placeholder:text-slate-400 outline-none transition-all focus:ring-2 focus:ring-brand-sky/25 ${
    invalid
      ? "border-red-400 focus:border-red-400"
      : "border-brand-line focus:border-brand-sky"
  }`;

export const Contact = () => {
  useSEO({
    title: "Contact Us — Book a Demo | CapableBee",
    description:
      "Book a demo today and see how CapableBee can turn conversations into leads.",
  });

  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [serverError, setServerError] = useState("");
  const [siteKey, setSiteKey] = useState(null);
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/config")
      .then((r) => {
        if (mounted) setSiteKey(r.data?.recaptchaSiteKey || null);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!siteKey || done) return;
    const render = () => {
      if (
        window.grecaptcha?.render &&
        captchaRef.current &&
        widgetIdRef.current === null
      ) {
        widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
          sitekey: siteKey,
        });
      }
    };
    if (window.grecaptcha) {
      window.grecaptcha.ready(render);
      return;
    }
    const existing = document.querySelector("script[data-recaptcha]");
    if (existing) {
      existing.addEventListener("load", () => window.grecaptcha.ready(render));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = "true";
    s.onload = () => window.grecaptcha.ready(render);
    document.head.appendChild(s);
  }, [siteKey, done]);

  const set = (name) => (e) =>
    setForm((f) => ({ ...f, [name]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "First name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.message.trim()) e.message = "Message is required";
    return e;
  };

  const getCaptchaToken = () => {
    if (widgetIdRef.current === null || !window.grecaptcha) return null;
    return window.grecaptcha.getResponse(widgetIdRef.current) || null;
  };

  const resetCaptcha = () => {
    if (widgetIdRef.current !== null && window.grecaptcha)
      window.grecaptcha.reset(widgetIdRef.current);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (sending) return;
    const e = validate();
    setErrors(e);
    setServerError("");
    if (Object.keys(e).length) return;

    const recaptcha_token = getCaptchaToken();
    if (siteKey && !recaptcha_token) {
      setServerError("Please complete the reCAPTCHA check.");
      return;
    }

    setSending(true);
    try {
      await api.post("/contact", { ...form, recaptcha_token });
      setSubmittedName(form.first_name);
      setForm(INITIAL);
      resetCaptcha();
      setDone(true);
      toast.success("Message received — we will be in touch shortly.");
    } catch (err) {
      resetCaptcha();
      setServerError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          "Something went wrong. Please try again.",
      );
      toast.error("Message could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-[72px]" data-testid="contact-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-ice via-brand-iceSoft to-white py-16 lg:py-20">
        <div
          className="bg-grid mask-fade-b pointer-events-none absolute inset-0"
          aria-hidden="true"
        />
        <div className="container-x relative">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-lineBlue bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-skyDeep shadow-card">
                <span
                  className="h-1.5 w-1.5 rounded-full bg-brand-honey"
                  aria-hidden="true"
                />
                Contact Us
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl">
                Let's talk about your business
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-base leading-relaxed text-brand-body/90 sm:text-lg">
                Book a demo today and see how CapableBee can turn conversations
                into leads.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="container-x grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div className="space-y-5">
            <Reveal data-testid="contact-info-email">
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-4 rounded-3xl border border-brand-line bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-widest text-slate-400">
                    Email us
                  </span>
                  <span className="block text-base font-bold text-brand-navy">
                    {SITE.email}
                  </span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.08} data-testid="contact-info-phone">
              <a
                href={`tel:${SITE.phone.replace(/[^+\d]/g, "")}`}
                className="flex items-center gap-4 rounded-3xl border border-brand-line bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-widest text-slate-400">
                    Call us
                  </span>
                  <span className="block text-base font-bold text-brand-navy">
                    {SITE.phone}
                  </span>
                </span>
              </a>
            </Reveal>

            <Reveal delay={0.16} data-testid="contact-info-note">
              <div className="rounded-3xl border border-brand-lineBlue bg-brand-iceSoft p-6">
                <p className="text-sm font-bold text-brand-navy">
                  What happens next?
                </p>
                <p className="mt-2 text-sm leading-relaxed text-brand-body/80">
                  After submitting, you will be contacted to discuss your
                  inquiry and how we can assist you.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            {done ? (
              <div
                className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl border border-brand-line bg-white p-10 text-center shadow-card"
                data-testid="contact-form-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 text-2xl font-extrabold text-brand-navy">
                  Thanks{submittedName ? `, ${submittedName}` : ""} — message
                  received!
                </h2>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                  You will be contacted to discuss your inquiry and how we can
                  assist you.
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  data-testid="contact-send-another"
                  className="mt-7 inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-6 py-3 text-sm font-bold text-brand-navy transition-all hover:-translate-y-0.5 hover:shadow-card"
                >
                  <RotateCcw className="h-4 w-4" />
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                noValidate
                className="rounded-3xl border border-brand-line bg-white p-7 shadow-card sm:p-9"
                data-testid="contact-form"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="cf-first"
                      className="mb-2 block text-sm font-bold text-brand-navy"
                    >
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cf-first"
                      type="text"
                      value={form.first_name}
                      onChange={set("first_name")}
                      className={inputCls(errors.first_name)}
                      placeholder="Jane"
                      data-testid="contact-first-name-input"
                    />
                    {errors.first_name && (
                      <p
                        className="mt-1.5 text-xs font-semibold text-red-500"
                        data-testid="contact-first-name-error"
                      >
                        {errors.first_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cf-email"
                      className="mb-2 block text-sm font-bold text-brand-navy"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputCls(errors.email)}
                      placeholder="jane@company.com"
                      data-testid="contact-email-input"
                    />
                    {errors.email && (
                      <p
                        className="mt-1.5 text-xs font-semibold text-red-500"
                        data-testid="contact-email-error"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cf-phone"
                      className="mb-2 block text-sm font-bold text-brand-navy"
                    >
                      Mobile Number
                    </label>
                    <input
                      id="cf-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls(false)}
                      placeholder="+1 555 000 0000"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="cf-subject"
                      className="mb-2 block text-sm font-bold text-brand-navy"
                    >
                      Subject
                    </label>
                    <input
                      id="cf-subject"
                      type="text"
                      value={form.subject}
                      onChange={set("subject")}
                      className={inputCls(false)}
                      placeholder="Book a demo"
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="cf-message"
                    className="mb-2 block text-sm font-bold text-brand-navy"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="cf-message"
                    rows="5"
                    value={form.message}
                    onChange={set("message")}
                    className={inputCls(errors.message)}
                    placeholder="Tell us about your business and call volume…"
                    data-testid="contact-message-input"
                  />
                  {errors.message && (
                    <p
                      className="mt-1.5 text-xs font-semibold text-red-500"
                      data-testid="contact-message-error"
                    >
                      {errors.message}
                    </p>
                  )}
                </div>

                {siteKey && (
                  <div className="mt-6" data-testid="contact-recaptcha">
                    <div ref={captchaRef} />
                  </div>
                )}

                {serverError && (
                  <p
                    role="alert"
                    className="mt-4 text-sm font-semibold text-red-500"
                    data-testid="contact-form-error"
                  >
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-sky px-8 py-4 text-sm font-extrabold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-brand-skyDeep hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
};
